import { mapState, mapGetters } from 'vuex'
import { find } from 'lodash'
import { positionAcceptedCountries, allCountries } from '@/lib/countries/countries.js'
import { getExchangesFromCountry } from '@/lib/exchanges/exchanges'
import { supportedCurrencies } from '@/lib/currencies/currencies'
import {
  getPositionTypes,
  isPositionDerivative,
  getPositionOrDerivativePositionType,
} from '@/lib/positions/positions.js'
import InfoIcon from '@/views/components/common/InfoIcon'
import CurrencySelector from '@/views/components/common/CurrencySelector'
import CountrySelector from '@/views/components/common/CountrySelector'
import CurrencyInput from '@/views/components/common/CurrencyInput'
import TagBox from '@/views/components/common/TagBox'
import SectorBox from '@/views/components/common/SectorBox'

const ACCEPTED_COMMODITY_POSITION_TICKERS = [
  'ALI',
  'BO',
  'BZ',
  'CB',
  'CC',
  'CL',
  'CT',
  'DY',
  'EH',
  'FC',
  'GC',
  'GF',
  'HE',
  'HG',
  'KC',
  'LB',
  'MZN',
  'NG',
  'NICKEL',
  'PA',
  'PL',
  'RU',
  'SB',
  'SI',
  'SM',
  'TIN',
  'W',
  'ZC',
  'ZL',
  'ZM',
  'ZO',
  'ZS',
]

export default {
  components: { CurrencySelector, CountrySelector, InfoIcon, CurrencyInput, TagBox, SectorBox },
  data: () => ({
    myPosition: null,
    moreConfiguration: false,
    allCountries,
  }),
  props: {
    position: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    positionTypesFilter: {
      type: Array,
      default: () => [],
    },
    positionCurrenciesFilter: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters(['isAdminOrTesterRoleUser']),
    positionCountryIsUserCountry() {
      return this.myPosition.country === this.user.country
    },
    showCustomDefaultDividendOriginRetentionPercent() {
      const dividendPositionTypes = getPositionTypes({ model: 'dividendPositionTypes' })
      return dividendPositionTypes.indexOf(this.position.type) > -1
    },
    showCustomDefaultDividendReturnPercent() {
      return this.showCustomDefaultDividendOriginRetentionPercent
    },
    positionTypes() {
      const positionTypes =
        this.positionTypesFilter && this.positionTypesFilter.length
          ? this.positionTypesFilter
          : getPositionTypes({
              model: this.type,
            })

      return positionTypes
        .map((type) => {
          return {
            text: this.$t(`views.orders.orderModal.createNewPosition.type.${type}`),
            value: type,
          }
        })
        .filter((item) => {
          return ['cfd'].indexOf(item.value) === -1 || this.isAdminOrTesterRoleUser
        })
    },
    derivativeOptionsPositionTypes() {
      return getPositionTypes({
        model: 'derivativeOptions',
      }).map((type) => {
        return {
          text: this.$t(`views.orders.orderModal.createNewPosition.type.${type}`),
          value: type,
        }
      })
    },
    isDerivative() {
      return isPositionDerivative(this.myPosition)
    },
    positionOrDerivativePositionType() {
      if (!this.myPosition) return
      return getPositionOrDerivativePositionType(this.myPosition)
    },
    showSector() {
      return (
        [
          'stock',
          'fund',
          'right',
          'etf',
          'plan',
          'bond',
          'putOption',
          'callOption',
          'custom',
        ].indexOf(this.positionOrDerivativePositionType) > -1
      )
    },
    showCountrySelector() {
      return (
        ['stock', 'right', 'etf', 'plan', 'bond', 'putOption', 'callOption'].indexOf(
          this.positionOrDerivativePositionType
        ) > -1
      )
    },
    showExposureCountrySelector() {
      return (
        this.showCountrySelector || ['fund'].indexOf(this.positionOrDerivativePositionType) > -1
      )
    },
    showExchangeSelector() {
      return (
        this.showCountrySelector &&
        ['putOption', 'callOption', 'bond'].indexOf(this.positionOrDerivativePositionType) === -1
      )
    },
    countrySelectorList() {
      // if (['bond'].indexOf(this.positionOrDerivativePositionType) > -1)
      //   return positionAcceptedCountries.filter((elem) => elem.countryCode !== 'US')
      return positionAcceptedCountries
    },
    tickerLabelTranslation() {
      return this.$t(`views.stocks.stockModal.ticker.${this.positionOrDerivativePositionType}`)
    },
    tickerLabelHelp() {
      if (!this.positionOrDerivativePositionType) return ''
      return this.$t(`views.stocks.stockModal.tickerHelp.${this.positionOrDerivativePositionType}`)
    },
    commodityPositionTickers() {
      return ACCEPTED_COMMODITY_POSITION_TICKERS.map((ticker) => {
        return {
          key: ticker,
          value: ticker,
          text: this.$t(`common.commodities.${ticker}`) + ' (' + ticker + ')',
        }
      })
    },
    cashPositionTickers() {
      return supportedCurrencies.filter((currency) => currency !== this.user.baseCurrency)
    },
    exchangeSelectorList() {
      if (this.myPosition.country) {
        return getExchangesFromCountry(this.myPosition.country).map((exchange) => {
          return {
            key: exchange,
            value: exchange,
            text: this.$t(`common.exchanges.${this.myPosition.country}.${exchange}`),
          }
        })
      } else {
        return null
      }
    },
    // categorySelectorList() {
    //   const CATEGORY_LIST = ['notFinancial', 'financial', 'publicCorporation']
    //   if (this.user.country !== this.myPosition.country) CATEGORY_LIST.push('multilateralOrganism')
    //   return CATEGORY_LIST.map((elem) => {
    //     return {
    //       key: elem,
    //       value: elem,
    //       text: this.$t(`views.stocks.stockModal.category.list.${elem}`),
    //     }
    //   })
    // },
  },
  watch: {
    positionOrDerivativePositionType: {
      handler() {
        if (this.editing) return

        if (this.positionOrDerivativePositionType === 'cash') {
          this.$set(this.myPosition, 'currency', this.user.baseCurrency)
          this.$set(this.myPosition, 'country', 'FX')
          this.$set(this.myPosition, 'exposureCountry', 'FX')
          this.$set(this.myPosition, 'exchange', 'FX')
        } else if (this.positionOrDerivativePositionType === 'crypto') {
          this.$set(this.myPosition, 'currency', this.user.baseCurrency)
          this.$set(this.myPosition, 'country', 'CC')
          this.$set(this.myPosition, 'exposureCountry', 'CC')
          this.$set(this.myPosition, 'exchange', 'CC')
        } else if (this.positionOrDerivativePositionType === 'index') {
          this.$set(this.myPosition, 'country', 'INDEX')
          this.$set(this.myPosition, 'exposureCountry', 'INDEX')
          this.$set(this.myPosition, 'exchange', 'INDEX')
        } else if (this.positionOrDerivativePositionType === 'commodity') {
          this.$set(this.myPosition, 'currency', this.user.baseCurrency)
          this.$set(this.myPosition, 'country', 'COMM')
          this.$set(this.myPosition, 'exposureCountry', 'COMM')
          this.$set(this.myPosition, 'exchange', 'COMM')
        } else {
          if (!this.isImportPositionForm) {
            this.$set(this.myPosition, 'country', undefined)
            this.$set(this.myPosition, 'exposureCountry', undefined)
          }
        }
      },
    },
    'myPosition.country': {
      handler() {
        this.updateCurrencyWithCountry()

        if (this.myPosition && this.myPosition.country && !this.myPosition.exposureCountry) {
          this.$set(this.myPosition, 'exposureCountry', this.myPosition.country)
        }
        if (this.myPosition && this.myPosition.country && this.exchangeSelectorList.length) {
          if (!this.exchangeSelectorList.some((elem) => elem.value === this.myPosition.exchange)) {
            this.$set(this.myPosition, 'exchange', this.exchangeSelectorList[0].value)
          }
        }
      },
      immediate: true,
    },
    'myPosition.ticker': {
      handler() {
        if (this.myPosition.ticker)
          this.$set(this.myPosition, 'ticker', this.myPosition.ticker.toUpperCase())
      },
    },
    myPosition: {
      handler() {
        if (!this.myPosition) return
        if (JSON.stringify(this.myPosition) === JSON.stringify(this.position)) return
        this.$emit('update:position', this.myPosition)
      },
      deep: true,
    },
    position: {
      handler() {
        this.myPosition = JSON.parse(JSON.stringify(this.position))
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    updateCurrencyWithCountry() {
      if (this.editing) return

      if (this.myPosition && this.myPosition.country && this.showCountrySelector) {
        const country = find(positionAcceptedCountries, { countryCode: this.myPosition.country })
        this.$set(this.myPosition, 'currency', country.currencyCode)

        if (
          !this.isImportPositionFormInDict &&
          (!this.isImportPositionForm || !this.myPosition.exchange) &&
          this.showExchangeSelector &&
          this.exchangeSelectorList &&
          this.exchangeSelectorList.length
        ) {
          this.$set(this.myPosition, 'exchange', this.exchangeSelectorList[0].value)
        }
      }
    },
    resetForm() {
      this.moreConfiguration = false
    },
  },
}
